import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL, fetchFile } from '@ffmpeg/util';

// 單例模式
let ffmpeg: FFmpeg | null = null;
let isLoaded = false;

export function isFFmpegLoaded(): boolean {
  return isLoaded;
}

// 用於測試：重置 singleton 狀態
export function resetFFmpeg(): void {
  ffmpeg = null;
  isLoaded = false;
}

export async function loadFFmpeg(onProgress?: (progress: number) => void): Promise<FFmpeg> {
  if (isLoaded && ffmpeg) {
    console.log('FFmpeg already loaded');
    return ffmpeg;
  }

  ffmpeg = new FFmpeg();
  
  // 添加日誌監聽
  ffmpeg.on('log', ({ message }) => {
    console.log('[FFmpeg]:', message);
  });
  
  ffmpeg.on('progress', ({ progress }) => {
    console.log('[FFmpeg Progress]:', Math.round(progress * 100) + '%');
    onProgress?.(Math.round(progress * 100));
  });

  // 檢測環境是否支援 Multi-Threading
  const isMTSupported = window.crossOriginIsolated && typeof SharedArrayBuffer !== 'undefined';
  
  console.log('FFmpeg Environment:', {
    crossOriginIsolated: window.crossOriginIsolated,
    hasSharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
    isMTSupported
  });

  const baseURL_MT = 'https://unpkg.com/@ffmpeg/core-mt@0.12.6/dist/esm';
  const baseURL_ST = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';

  try {
    if (isMTSupported) {
      console.log('Loading FFmpeg (MT) from:', baseURL_MT);
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL_MT}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL_MT}/ffmpeg-core.wasm`, 'application/wasm'),
        workerURL: await toBlobURL(`${baseURL_MT}/ffmpeg-core.worker.js`, 'text/javascript'),
      });
    } else {
      console.warn('Environment does not support MT (requires HTTPS + Headers). Falling back to ST.');
      console.log('Loading FFmpeg (ST) from:', baseURL_ST);
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL_ST}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL_ST}/ffmpeg-core.wasm`, 'application/wasm'),
        // ST 版本不需要 workerURL
      });
    }
    
    isLoaded = true;
    console.log('FFmpeg loaded successfully!');
    return ffmpeg;
  } catch (err) {
    console.error('FFmpeg load error:', err);
    throw err;
  }
}

export function getFFmpeg(): FFmpeg {
  if (!ffmpeg) {
    throw new Error('FFmpeg not loaded. Call loadFFmpeg() first.');
  }
  return ffmpeg;
}

/**
 * 將 WebM 轉換為 MP3
 */
export async function convertToMp3(
  webmBlob: Blob,
  onProgress?: (progress: number) => void
): Promise<Blob> {
  const ffmpeg = await loadFFmpeg();

  // 設置進度回調
  ffmpeg.on('progress', ({ progress }) => {
    onProgress?.(Math.round(progress * 100));
  });

  // 寫入輸入檔案 - 根據 blob type 判斷副檔名
  const inputExt = webmBlob.type.includes('mp4') ? 'mp4' : 'webm';
  const inputName = `input.${inputExt}`;
  
  const inputData = await fetchFile(webmBlob);
  await ffmpeg.writeFile(inputName, inputData);

  // 執行轉換
  const args = [
    '-i', inputName,
    '-vn',                    // 忽略視頻
    '-acodec', 'libmp3lame',  // MP3 編碼器
    '-af', 'volume=1.41,pan=mono|c0=0.5*c0+0.5*c1', // 加大音量增益 (1.41x / +3dB) 並準確混音
    '-b:a', '192k',           // 比特率
    '-ar', '44100',           // 採樣率
    '-y',                     // 強制覆蓋
    'output.mp3'
  ];

  console.log('FFmpeg command:', args.join(' '));
  const result = await ffmpeg.exec(args);
  
  // 檢查退出代碼
  if (result !== 0) {
    throw new Error(`FFmpeg conversion failed with exit code ${result}`);
  }

  // 讀取輸出檔案
  const outputData = await ffmpeg.readFile('output.mp3');
  
  // 清理
  await ffmpeg.deleteFile(inputName);
  await ffmpeg.deleteFile('output.mp3');

  // 轉換為 Blob
  let blobData: BlobPart;
  if (outputData instanceof Uint8Array) {
    // 複製數據以確保類型兼容
    blobData = new Uint8Array(outputData).buffer;
  } else if (typeof outputData === 'string') {
    blobData = outputData;
  } else {
    blobData = outputData as ArrayBuffer;
  }
  return new Blob([blobData], { type: 'audio/mp3' });
}

// 質量配置 - 統一使用 48kHz 採樣率，與前端採集保持一致
const qualityConfigs = {
  standard: { sampleRate: 48000, bitrate: 128 },  // 統一 48kHz，減少重採樣損失
  high: { sampleRate: 48000, bitrate: 256 },
  lossless: { sampleRate: 48000, bitrate: 0 },
};

// MIME 類型對應（導出供下載使用）
export const mimeTypes: Record<string, string> = {
  webm: 'audio/webm',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  aac: 'audio/aac',
};

// 文件擴展名對應（導出供下載使用）
export const fileExtensions: Record<string, string> = {
  webm: '.webm',
  mp3: '.mp3',
  wav: '.wav',
  aac: '.aac',
};

/**
 * 通用音頻轉換函數
 */
export async function convertAudio(
  webmBlob: Blob,
  options: { format: 'mp3' | 'wav' | 'aac'; quality: 'standard' | 'high' | 'lossless' },
  onProgress?: (progress: number) => void
): Promise<Blob> {
  // 檢查文件大小，WASM 有內存限制
  const MAX_SIZE = 200 * 1024 * 1024; // 200MB
  if (webmBlob.size > MAX_SIZE) {
    throw new Error('錄音文件過大（超過 200MB），請縮短錄音時長或選擇壓縮格式');
  }

  const ffmpeg = await loadFFmpeg();
  const { format, quality } = options;
  const config = qualityConfigs[quality];

  // 設置進度回調
  ffmpeg.on('progress', ({ progress }) => {
    onProgress?.(Math.round(progress * 100));
  });
  
  // 寫入輸入檔案 - 自適應副檔名
  const inputExt = webmBlob.type.includes('mp4') ? 'mp4' : 'webm';
  const inputName = `input.${inputExt}`;
  
  const inputData = await fetchFile(webmBlob);
  await ffmpeg.writeFile(inputName, inputData);

  // 構建 FFmpeg 參數
  const outputFile = `output.${format}`;
  const args: string[] = ['-i', inputName, '-vn'];

  if (format === 'mp3') {
    args.push('-ar', String(config.sampleRate));
    args.push('-af', 'volume=1.41,pan=mono|c0=0.5*c0+0.5*c1'); // 1.41x 增益
    args.push('-c:a', 'libmp3lame');
    args.push('-b:a', `${config.bitrate}k`);
  } else if (format === 'wav') {
    args.push('-ar', String(config.sampleRate));
    args.push('-af', 'volume=1.41,pan=mono|c0=0.5*c0+0.5*c1'); // 1.41x 增益
    args.push('-c:a', 'pcm_s16le');
  } else if (format === 'aac') {
    args.push('-ar', String(config.sampleRate));
    args.push('-af', 'volume=1.41,pan=mono|c0=0.5*c0+0.5*c1'); // 1.41x 增益
    args.push('-c:a', 'aac');
    args.push('-b:a', `${config.bitrate}k`);
  }

  args.push('-y'); // 強制覆蓋
  args.push(outputFile);

  console.log('FFmpeg convertAudio command:', args.join(' '));

  // 執行轉換
  const result = await ffmpeg.exec(args);

  // 檢查退出代碼
  if (result !== 0) {
    throw new Error(`FFmpeg conversion failed with exit code ${result}`);
  }

  // 讀取輸出檔案
  const outputData = await ffmpeg.readFile(outputFile);

  // 清理
  await ffmpeg.deleteFile(inputName);
  await ffmpeg.deleteFile(outputFile);

  // 轉換為 Blob
  let blobData: BlobPart;
  if (outputData instanceof Uint8Array) {
    blobData = new Uint8Array(outputData).buffer;
  } else if (typeof outputData === 'string') {
    blobData = outputData;
  } else {
    blobData = outputData as ArrayBuffer;
  }

  return new Blob([blobData], { type: mimeTypes[format] });
}

/**
 * Merge multiple audio blobs into a single file
 * Uses FFmpeg concat demuxer with PCM re-encoding for seamless timestamps
 */
export const mergeAudio = async (
  blobs: Blob[],
  onProgress?: (progress: number) => void
): Promise<Blob> => {
  const ffmpeg = await loadFFmpeg();
  
  try {
    const inputFiles: string[] = [];
    
    // Write all blobs to FS
    for (let i = 0; i < blobs.length; i++) {
        const filename = `part${i}.webm`; // Assuming WebM input from MediaRecorder
        const data = await fetchFile(blobs[i]);
        await ffmpeg.writeFile(filename, data);
        inputFiles.push(filename);
    }
    
    // Create concat list file
    const concatList = inputFiles.map(f => `file '${f}'`).join('\n');
    await ffmpeg.writeFile('concat_list.txt', concatList);
    
    ffmpeg.on('progress', ({ progress }) => {
      onProgress?.(progress * 100);
    });

    // Run concat with PCM re-encoding
    // 移除 -c copy，改用 PCM 轉碼以重建完整時間戳，解決暫停/續錄的 timestamp discontinuity 問題
    // -f concat: format
    // -safe 0: allow relative paths
    // -i concat_list.txt: input list
    // -c:a pcm_s16le: PCM 16-bit 編碼，確保無損且時間戳連續
    // -ar 48000: 統一採樣率為 48kHz
    await ffmpeg.exec([
        '-f', 'concat', 
        '-safe', '0', 
        '-i', 'concat_list.txt', 
        '-c:a', 'pcm_s16le',  // PCM 轉碼，重建時間戳
        '-af', 'volume=1.41,pan=mono|c0=0.5*c0+0.5*c1', // 1.41x 增益
        '-ar', '48000',       // 統一 48kHz 採樣率
        '-y',
        'merged_temp.wav'     // WAV 中間文件
    ]);
    
    // Read output
    const data = await ffmpeg.readFile('merged_temp.wav');
    const blob = new Blob([data as unknown as BlobPart], { type: 'audio/wav' });
    
    // Cleanup
    for (const f of inputFiles) await ffmpeg.deleteFile(f);
    await ffmpeg.deleteFile('concat_list.txt');
    await ffmpeg.deleteFile('merged_temp.wav');
    
    return blob;
    
  } catch (error) {
    console.error('FFmpeg merge error:', error);
    throw error;
  }
};
